// MACHINERY V2 (Stroje) — THE FLEET SHEET'S TWO APPLY MODELS (M2.16a).
//
// >>> THIS FILE IS THE SEAM. <<< Which control is STAGED and which is LIVE is
// decided HERE and nowhere else. FleetFilterSheet.tsx and fleetSheetSections.tsx
// call named setters and read what this hook hands them; neither decides, and
// neither has to change if a control ever moves from one model to the other.
//
// ============================================================
//  THE SPLIT IS BY CONTROL TYPE (owner ruling, M2.16a fix pass)
// ============================================================
// FILTER FACETS — STAV / TYP STROJA (+ vyrobca) / LOKACIA — are STAGED into
// `draft` and applied by the footer's primary "Zobrazit n strojov". Zavriet,
// Esc and the backdrop discard; re-opening seeds fresh from applied state.
//
// MODE CONTROLS — ZOSKUPIT PODLA / ZOBRAZENIE / ROZSAH — apply LIVE, on tap,
// exactly as the desktop toolbar applies them (the FleetToolbar branch in
// FleetShell.tsx:154-170, re-read at commit time). They are exposed as `modes`,
// which carries the APPLIED value plus a setter that writes straight through.
//
// >>> WHY THE SPLIT, AND WHY IT IS NOT A TASTE CALL. <<< This codebase already
// says, twice and independently, that those three are NOT filters:
// fleetActiveFilters.ts:19 ("Group-by, view and scope are not filters at all")
// and `clearAll` below. Staging a non-filter alongside the filters produced
// three defects of one shape — `data` is computed for the APPLIED scope, so a
// PENDING scope made the sheet's own option lists, its counts and its preview
// all describe a fleet the page was not showing, and the preview dropped
// `states` in a case `commit` then dispatched them anyway. With scope live,
// `data` and `scope` always agree, and all three dissolve instead of each
// needing its own guard.
//
// ============================================================
//  SEEDING — ON THE OPEN TRANSITION, AND ONLY THERE
// ============================================================
// Re-opening the sheet must show the APPLIED facets, freshly seeded. The
// failure this forbids: tick three states, press Zavriet (discard), re-open,
// and see those three still ticked — a sheet misrepresenting what is actually
// filtering the fleet behind it, the same class of bug as a form that re-opens
// holding a cancelled draft.
//
// The seed is therefore taken in the RENDER BODY, keyed on `open !== prevOpen`
// (React's documented "adjusting state when a prop changes" pattern), NOT in an
// effect:
//   · it is exact about the trigger — an OPEN TRANSITION, not a render and not
//     a dependency change. The 60 s telemetry tick replaces `data` on this
//     page every minute; an effect seeded from applied state would need `data`
//     nowhere in its dependency list to survive that, and the next person to
//     add a dependency would silently break it. There is no dependency list
//     here to get wrong;
//   · it is synchronous — the first frame of the open sheet already shows the
//     seeded draft, where an effect would paint one frame of the previous one.
//
// DISCARD IS THE ABSENCE OF A WRITE. Zavriet / Esc / backdrop just close; the
// stale draft is left in state and overwritten by the next open. That is why
// there is no `discard()` to forget to call.
//
// RULE (d) — A SHEET HOLDING USER WORK NEVER DISCARDS IT ON FAILURE. Pending
// selections ARE user work. Nothing below writes `draft` except a user gesture
// and the open transition, so a re-render, the 60 s tick, a failed fetch and a
// parent state change all leave it exactly as the user left it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { MachinesQuery } from "../../models/models";
import { changeQuery } from "../../customerMachinerySlice";
import {
  MachineryV2FleetView,
  MachineryV2GroupBy,
  setFleetLocationFilter,
  setFleetStatusFilters,
  setFleetView,
} from "../machineryV2UiSlice";
import { MachineryV2CardState } from "./cardStates";
import { FleetPageData } from "./fleetPageData";
import { FleetScope } from "./FleetToolbar";
import { applyClientFilters, flattenGroups } from "./fleetSelectors";

/** The STAGED half — the three filter facets and nothing else. Type and
 *  manufacturer share a section but are two fields. */
export interface FleetFilterDraft {
  states: MachineryV2CardState[];
  typeId?: string;
  manufacturerId?: string;
  location: string | null;
}

/** The LIVE half — applied value plus a write-through setter, one pair per mode
 *  control. Reading a value from here is reading APPLIED state: there is no
 *  such thing as a pending mode, which is what keeps `data` and these in step. */
export interface FleetSheetModes {
  groupBy: MachineryV2GroupBy;
  view: MachineryV2FleetView;
  scope: FleetScope;
  setGroupBy: (groupBy: MachineryV2GroupBy) => void;
  setView: (view: MachineryV2FleetView) => void;
  setScope: (scope: FleetScope) => void;
}

export interface FleetFilterDraftApi {
  draft: FleetFilterDraft;
  modes: FleetSheetModes;
  toggleState: (state: MachineryV2CardState) => void;
  selectType: (typeId?: string) => void;
  selectManufacturer: (id?: string) => void;
  selectLocation: (id: string | null) => void;
  /** Clears the staged FILTERS — see the note at its implementation. */
  clearAll: () => void;
  /** Exact count of what applying the draft would show. */
  previewCount: number;
  commit: () => void;
}

export interface FleetFilterDraftParams {
  open: boolean;
  data: FleetPageData;
  scope: FleetScope;
  /** Page state, not slice state — hence a callback rather than a dispatch. */
  onScopeChange: (scope: FleetScope) => void;
  /** MUST be the page's `changeGroupBy`, never `setFleetGroupBy`: changing the
   *  dimension also resets the collapse set and the V3 table sort
   *  (useFleetSectionState.ts's changeGroupBy, review F2). Dispatching the raw
   *  action here would leave a group collapsed under a dimension the user has
   *  left. */
  onGroupByChange: (groupBy: MachineryV2GroupBy) => void;
}

export const useFleetFilterDraft = ({
  open,
  data,
  scope,
  onScopeChange,
  onGroupByChange,
}: FleetFilterDraftParams): FleetFilterDraftApi => {
  const dispatch = useAppDispatch();
  // FIELD SELECTORS, NOT THE SLICE OBJECT. `useAppSelector((s) => s.customerMachinery)`
  // returns the slice itself, whose identity changes on ANY write to it — so
  // this hook used to re-render on `loading`, `machine` and `decommissionPreview`
  // too, inside a component that is mounted permanently. Same reasoning for the
  // four UI fields: `palette` and `railCollapsed` live in that slice as well.
  const query = useAppSelector((s) => s.customerMachinery.query);
  const appliedStates = useAppSelector((s) => s.machineryV2Ui.fleetStatusFilters);
  const appliedLocation = useAppSelector((s) => s.machineryV2Ui.fleetLocationFilter);
  const appliedGroupBy = useAppSelector((s) => s.machineryV2Ui.fleetGroupBy);
  const appliedView = useAppSelector((s) => s.machineryV2Ui.fleetView);

  // `changeQuery` REPLACES the query wholesale, and the search debounce writes
  // to the same object 400 ms after a keystroke — so the commit merges onto the
  // CURRENT query, never onto a snapshot captured at render time. Same fix, same
  // shape, as useFleetSearch.ts:42-43 (bug 1, "stale merge base").
  const queryRef = useRef<MachinesQuery>(query);
  queryRef.current = query;

  const applied: FleetFilterDraft = {
    states: appliedStates,
    typeId: query.typeId,
    manufacturerId: query.manufacturerId,
    location: appliedLocation,
  };

  const [draft, setDraft] = useState<FleetFilterDraft>(applied);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    // THE ONE SEED. See the header: an open transition, in the render body, with
    // no dependency list that a telemetry tick could appear in.
    if (open) setDraft(applied);
  }

  const patch = useCallback(
    (p: Partial<FleetFilterDraft>) => setDraft((d) => ({ ...d, ...p })),
    []
  );

  const toggleState = useCallback(
    (state: MachineryV2CardState) =>
      setDraft((d) => ({
        ...d,
        states: d.states.indexOf(state) >= 0
          ? d.states.filter((s) => s !== state)
          : [...d.states, state],
      })),
    []
  );

  /** Picking a type clears the manufacturer, exactly as the rail does
   *  (FleetShell.tsx's onSelectType) — the manufacturer list is DERIVED from the
   *  selected type (fleetSelectors.derivedManufacturers), so a carried-over
   *  manufacturer would filter by an option no longer on offer. */
  const selectType = useCallback(
    (typeId?: string) => patch({ typeId, manufacturerId: undefined }),
    [patch]
  );

  const selectManufacturer = useCallback(
    (id?: string) => patch({ manufacturerId: id }),
    [patch]
  );
  const selectLocation = useCallback((id: string | null) => patch({ location: id }), [patch]);

  /** THE LIVE HALF. Every setter is the write the desktop toolbar already
   *  makes, so the two surfaces cannot drift: `setGroupBy` is the PAGE's
   *  changeGroupBy (see the params note on why it may not be the raw action),
   *  `setView` is the dispatch FleetShell.tsx:164 makes, and `setScope` is the
   *  page's own scope state. */
  const modes: FleetSheetModes = {
    groupBy: appliedGroupBy,
    view: appliedView,
    scope,
    setGroupBy: onGroupByChange,
    setView: (view: MachineryV2FleetView) => dispatch(setFleetView(view)),
    setScope: onScopeChange,
  };

  /** THE STAGED SET, IN FULL. There is nothing here to leave out: group-by,
   *  view and scope are never written into `draft` at all (they apply live —
   *  see `modes`), so "filters only" describes what this sheet stages rather
   *  than a carve-out the next reader has to remember. */
  const clearAll = useCallback(
    () => patch({ states: [], location: null, typeId: undefined, manufacturerId: undefined }),
    [patch]
  );

  /**
   * THE PREVIEW — the SAME client-side faceting the page itself runs
   * (useFleetPageData's `visibleGroups`), over `data.flat`. EXACT, not an
   * estimate: `data` is built for the applied scope and scope can no longer be
   * pending, so the base set, the telemetry-derived states and the status rule
   * are the three inputs the real pipeline uses. The number the primary prints
   * is the number that appears once it is pressed.
   *
   * The status rule mirrors useFleetPageData.ts:163 exactly — telemetry exists
   * only in the active scope, so the decommissioned scope facets on no states.
   *
   * GATED ON `open`. The sheet is mounted permanently (FleetMobileChrome.tsx
   * :86-88) and is usually closed, while the telemetry tick hands `data.states`
   * a fresh Map every 60 s — so an ungated memo re-faceted the whole fleet once
   * a minute to produce a number nothing was rendering. While closed the honest
   * answer is what the grid is already showing.
   */
  const previewCount = useMemo(() => {
    if (!open) return data.visibleCount;
    return flattenGroups(
      applyClientFilters({ preview: data.flat }, data.states, {
        statusFilters: scope === "decommissioned" ? [] : draft.states,
        locationFilter: draft.location,
        typeId: draft.typeId,
        manufacturerId: draft.manufacturerId,
      })
    ).length;
  }, [open, draft, scope, data.flat, data.states, data.visibleCount]);

  /** FACETS ONLY — the mode controls have already applied themselves. */
  const commit = useCallback(() => {
    dispatch(setFleetStatusFilters(draft.states));
    dispatch(setFleetLocationFilter(draft.location));
    if (draft.typeId !== query.typeId || draft.manufacturerId !== query.manufacturerId) {
      dispatch(
        changeQuery({
          ...queryRef.current,
          typeId: draft.typeId,
          manufacturerId: draft.manufacturerId,
        })
      );
    }
  }, [draft, dispatch, query.typeId, query.manufacturerId]);

  return {
    draft,
    modes,
    toggleState,
    selectType,
    selectManufacturer,
    selectLocation,
    clearAll,
    previewCount,
    commit,
  };
};

export default useFleetFilterDraft;
